import BaseWizardController from "./base_wizard_controller"

/**
 * Public booking wizard controller — 3-step flow:
 *   Step 0: Dives (calendar + divers + products)
 *   Step 1: Review (order summary + accommodation section)
 *   Step 2: Payment (booker info + invoice summary + submit)
 *
 * Accommodation is fully owned by the `public-accommodation` controller
 * (modal + inline picker + canonical hidden inputs). The wizard delegates
 * to it via a Stimulus outlet only at submit time to guard against stale
 * or missing accommodation selections.
 *
 * On submit, creates booking server-side and redirects to confirmation page.
 *
 * Extends base with:
 *   - reCAPTCHA integration
 *   - Booker validation on payment step
 *   - Submit guard via public-accommodation outlet
 */
export default class extends BaseWizardController {
  static targets = [
    ...BaseWizardController.targets
  ]

  static values = {
    ...BaseWizardController.values,
    recaptchaSiteKey: { type: String, default: "" }
  }

  static outlets = ["public-accommodation"]

  connect() {
    this.recaptchaReady = false
    super.connect()
    this.initializeRecaptcha()
  }

  disconnect() {
    if (this.recaptchaCheckTimeout) {
      clearTimeout(this.recaptchaCheckTimeout)
    }
  }

  get divesControllerIdentifier() {
    return "public-dives"
  }

  // ===== Step Validation Override =====
  // New layout: 0=Dives, 1=Review, 2=Payment, 3=Done

  validateCurrentStep() {
    const currentStep = this.stepTargets[this.currentStepValue]
    if (!currentStep) return true

    switch (this.currentStepValue) {
      case 0: // Dives step
        return this.validateDivesStep(currentStep)
      case 1: // Review step — always valid
        return true
      case 2: // Payment step — validate booker info
        return this.validateBookerStep(currentStep)
      default:
        return true
    }
  }

  // ===== Step Transitions Override =====

  onStepTransition(fromStep, toStep) {
    if (toStep === 1) {
      // Entering Review step — coverage recompute is driven by public-dives
      // events (wired in HTML via data-action); nothing else needed here.
      this.buildReview()
    }
    if (toStep === 2) {
      // Entering Payment step
      this.onEnterPaymentStep()
      this.buildPaymentInvoice()
      this.prefillBookerFromPrimaryDiver()
    }
  }

  // Listener wired in HTML — refreshes review cards reactively when
  // dives change. Other coverage/picker logic lives in public-accommodation.
  onDivesChanged() {
    if (this.currentStepValue === 1) this.buildReview()
  }

  // Listener wired in HTML — fires when public-accommodation accepts a new
  // room (confirmRoom) or drops the current one (removeAccommodation).
  // Without this, a customer who entered Review without accommodation, then
  // came back to add it, would see the ORDER REVIEW total stay frozen on
  // the dive-only subtotal until they manually navigated steps.
  onAccommodationChanged() {
    if (this.currentStepValue === 1) this.buildReview()
    if (this.currentStepValue === 2) this.buildPaymentInvoice()
  }

  // ===== Prefill Booker from Primary Diver =====

  prefillBookerFromPrimaryDiver() {
    const nameField = this.element.querySelector('[name="booking[full_name]"]')
    if (!nameField || nameField.value.trim()) return // Don't overwrite if already filled

    const divesController = this._getDivesController()
    if (!divesController) return

    const primary = divesController.divers.find(d => d.position === 1)
    if (!primary) return

    const name = primary.label
    // Skip default placeholder labels like "Diver 1 Full Name"
    if (name && !name.match(/^Diver \d+ Full Name$/)) {
      nameField.value = name
    }
  }

  // ===== Review Cards Override — no Primary Guest, two-column Divers + Dates =====

  buildReviewCards() {
    if (!this.hasReviewCardsTarget) return

    const dateInputs = this.element.querySelectorAll('input[name="booking[dive_dates][]"]')
    const uniqueDates = [...new Set(Array.from(dateInputs).map(i => i.value))].sort()
    const formattedDates = this._formatDiveDates(uniqueDates)

    const divesController = this._getDivesController()
    const diverCount = divesController ? divesController.getDiverCount() : 1

    this.reviewCardsTarget.innerHTML = `
      <div class="row g-3">
        <div class="col-6">
          <div class="d-flex align-items-center gap-3 p-3 rounded" style="border: 1px solid rgba(255,255,255,0.08);">
            <div class="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style="width: 40px; height: 40px; background: rgba(172,165,60,0.2);">
              <i class="bi bi-people" style="color: var(--accent-color);"></i>
            </div>
            <div>
              <div class="small fw-bold text-uppercase text-accent" style="letter-spacing: 0.05em;">Divers</div>
              <div class="fw-bold">${diverCount} Diver${diverCount !== 1 ? "s" : ""}</div>
            </div>
          </div>
        </div>
        <div class="col-6">
          <div class="d-flex align-items-center gap-3 p-3 rounded" style="border: 1px solid rgba(255,255,255,0.08);">
            <div class="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style="width: 40px; height: 40px; background: rgba(172,165,60,0.2);">
              <i class="bi bi-calendar3" style="color: var(--accent-color);"></i>
            </div>
            <div>
              <div class="small fw-bold text-uppercase text-accent" style="letter-spacing: 0.05em;">Dive Dates</div>
              <div class="fw-bold">${formattedDates || "None"}</div>
            </div>
          </div>
        </div>
      </div>`
  }

  // Format an ordered list of YYYY-MM-DD dive dates into a compact human
  // string. Drops the year from every entry, shows it once at the end, and
  // groups consecutive same-month entries so a 7-date list reads
  // "May 24, 26, 28, 29, 30, Jun 1, 3, 2026" instead of wrapping onto
  // multiple lines with seven repeated years.
  _formatDiveDates(uniqueDates) {
    if (uniqueDates.length === 0) return "None"

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
    const parts = []
    let lastMonth = null
    let lastYear = null

    uniqueDates.forEach(v => {
      const d = new Date(v + "T00:00:00")
      const m = d.getMonth()
      const day = d.getDate()
      const year = d.getFullYear()
      if (m !== lastMonth) {
        parts.push(`${months[m]} ${day}`)
        lastMonth = m
      } else {
        parts.push(String(day))
      }
      lastYear = year
    })

    return `${parts.join(", ")}, ${lastYear}`
  }

  // ===== Booker Validation (on payment step, not guest step) =====

  validateBookerStep(step) {
    const nameField = this.element.querySelector('[name="booking[full_name]"]')
    const emailField = this.element.querySelector('[name="booking[email]"]')
    const phoneField = this.element.querySelector('[name="booking[phone]"]')

    if (!nameField || !nameField.value.trim()) {
      this.showStepError(step, "Please enter your full name.")
      return false
    }

    const hasEmail = emailField && emailField.value.trim()
    const hasPhone = phoneField && phoneField.value.trim()
    if (!hasEmail && !hasPhone) {
      this.showStepError(step, "Please provide an email address or phone number.")
      return false
    }

    this.clearStepError(step)
    return true
  }

  // ===== Build Payment Invoice Display =====
  //
  // Dive lines come from the public-dives controller. Accommodation lines
  // are delegated to public-accommodation via the Stimulus outlet contract
  // (getInvoiceLines) so the Payment-step total stays in parity with the
  // Review-step pricing breakdown.

  buildPaymentInvoice() {
    const divesController = this._getDivesController()
    if (!divesController) return

    const summary = divesController.getSelectionSummary()
    let totalCents = divesController.getGrandTotal()

    const invoiceTarget = this.element.querySelector("[data-invoice-lines]")
    if (!invoiceTarget) return

    let html = ""
    summary.forEach(group => {
      if (group.items.length === 0) return
      html += `<div class="small fw-bold text-accent text-uppercase mb-1 mt-3" style="letter-spacing: 0.05em;">${this._escapeHtml(group.label)}</div>`
      group.items.forEach(item => {
        const lineTotal = item.qty * item.price_cents
        html += `
          <div class="d-flex justify-content-between align-items-center py-1 small">
            <span>${this._escapeHtml(item.label)}${item.qty > 1 ? ` <span style="color: rgba(255,255,255,0.4);">×${item.qty}</span>` : ""}</span>
            <span class="fw-medium">$${(lineTotal / 100).toFixed(2)}</span>
          </div>`
      })
    })

    // Append accommodation lines (delegated to public-accommodation via outlet).
    if (this.hasPublicAccommodationOutlet) {
      const accom = this.publicAccommodationOutlet.getInvoiceLines()
      if (accom && accom.lines.length > 0) {
        html += `<div class="small fw-bold text-accent text-uppercase mb-1 mt-3" style="letter-spacing: 0.05em;">Accommodation</div>`
        accom.lines.forEach(line => {
          html += `
            <div class="d-flex justify-content-between align-items-center py-1 small">
              <span>${this._escapeHtml(line.label)} <span style="color: rgba(255,255,255,0.4);">${this._escapeHtml(line.qtyLabel)}</span></span>
              <span class="fw-medium">$${(line.amount_cents / 100).toFixed(2)}</span>
            </div>`
        })
        totalCents += accom.totalCents

        // Mirror the confirmation/show breakdown: itemize the inclusive
        // accommodation total into Room (ex-tax) + Service Charge + T-GST +
        // Green Tax so the customer never sees a different decomposition
        // before/after submit. Source of truth is public_accommodation's
        // _inclusiveCascadeCents (which mirrors BusinessEntity#inclusive_total_cents).
        const bd = this.publicAccommodationOutlet.getTaxBreakdown()
        if (bd && bd.taxesTotalCents > 0) {
          html += `<div class="mt-1 small" data-test="accommodation-tax-breakdown" style="color: rgba(255,255,255,0.65);">`
          html += this._taxRow(`Room (ex-tax)`, bd.baseCents, "rgba(255,255,255,0.45)")
          if (bd.greenCents > 0) {
            html += this._taxRow(
              `Green Tax <span style="color: rgba(255,255,255,0.35);">(${bd.adults} adults &times; ${bd.nights} nights)</span>`,
              bd.greenCents
            )
          }
          if (bd.tgstCents > 0) html += this._taxRow(`T-GST (${Math.round(bd.tgstPct)}%)`, bd.tgstCents)
          if (bd.scCents > 0) html += this._taxRow(`Service Charge (${Math.round(bd.scPct)}%)`, bd.scCents)
          html += `</div>`
        }
      }
    }

    invoiceTarget.innerHTML = html

    const totalEl = this.element.querySelector("[data-invoice-total]")
    if (totalEl) {
      totalEl.textContent = `$${(totalCents / 100).toFixed(2)}`
    }
  }

  _taxRow(label, cents, color = null) {
    const style = color ? ` style="color: ${color};"` : ""
    return `<div class="d-flex justify-content-between"${style}><span>${label}</span><span>$${(cents / 100).toFixed(2)}</span></div>`
  }

  // Override: fold accommodation lines (and the trailing inclusive total)
  // into the Review-step ORDER REVIEW table so the customer-visible total
  // matches what they'll see on the Payment step. Without this the Review
  // table would understate the booking — accommodation is owned by a
  // sibling controller (public-accommodation) and the base Review builder
  // sees only dive selections.
  _buildExtraReviewItems() {
    if (!this.hasPublicAccommodationOutlet) return null
    const accom = this.publicAccommodationOutlet.getInvoiceLines()
    if (!accom || accom.lines.length === 0) return null

    let html = `
      <div class="px-6 py-2.5 bg-gray-50 dark:bg-base-900 border-b border-gray-100 dark:border-base-700">
        <span class="text-xs font-bold text-blue-500 dark:text-blue-300 uppercase tracking-wider">Accommodation</span>
      </div>`
    accom.lines.forEach(line => {
      html += `
        <div class="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 px-6 py-4 items-center">
          <div class="sm:col-span-6">
            <div class="font-semibold text-gray-900 dark:text-white inline-flex items-center gap-1.5">
              <i class="bi bi-building text-accent"></i> ${this._escapeHtml(line.label)}
            </div>
            <div class="text-xs text-gray-400 dark:text-base-300 mt-0.5">${this._escapeHtml(line.qtyLabel)}</div>
          </div>
          <div class="sm:col-span-3 text-center">
            <span class="text-sm font-medium text-gray-900 dark:text-white">1</span>
          </div>
          <div class="sm:col-span-3 sm:text-right">
            <span class="text-gray-400 dark:text-base-300 text-sm mr-1">$</span>
            <span class="text-sm font-semibold text-gray-900 dark:text-white">${(line.amount_cents / 100).toFixed(2)}</span>
          </div>
        </div>`
    })
    return { html, totalCents: accom.totalCents }
  }

  // ===== reCAPTCHA =====

  initializeRecaptcha() {
    const siteKey = this.recaptchaSiteKeyValue
    if (!siteKey) {
      this.recaptchaReady = false
      return
    }

    if (typeof grecaptcha !== "undefined" && grecaptcha.ready) {
      grecaptcha.ready(() => {
        this.recaptchaReady = true
      })
      return
    }

    let attempts = 0
    const maxAttempts = 20
    const pollForRecaptcha = () => {
      attempts++
      if (typeof grecaptcha !== "undefined" && grecaptcha.ready) {
        grecaptcha.ready(() => { this.recaptchaReady = true })
      } else if (attempts < maxAttempts) {
        this.recaptchaCheckTimeout = setTimeout(pollForRecaptcha, 250)
      } else {
        this.recaptchaReady = false
      }
    }
    this.recaptchaCheckTimeout = setTimeout(pollForRecaptcha, 250)
  }

  // ===== Form Submission =====
  // Saves booking → server redirects to checkout

  async submitBooking(event) {
    event.preventDefault()

    if (!this.hasFormTarget) return

    // Validate booker fields before submitting
    const paymentStep = this.stepTargets[2]
    if (!this.validateBookerStep(paymentStep)) return

    // Hard-assert outlet presence: silently skipping when the outlet is
    // missing would let a stale-accom submit through. If the outlet
    // genuinely isn't there, that's a markup bug and we want to know.
    if (!this.hasPublicAccommodationOutlet) {
      console.error("public-wizard: public-accommodation outlet missing; refusing to submit")
      this._resetSubmitButton()
      return
    }
    const verdict = this.publicAccommodationOutlet.canSubmit()
    if (!verdict.ok) {
      this._resetSubmitButton()
      this.showStep(1)
      verdict.focusElement?.scrollIntoView({ block: "center" })
      return
    }

    // Sync primary diver name from booker name
    const nameField = this.element.querySelector('[name="booking[full_name]"]')
    const divesController = this._getDivesController()
    if (divesController && nameField?.value) {
      divesController.setPrimaryDiver(null, nameField.value)
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.textContent = "Processing..."
    }

    // Execute reCAPTCHA
    if (this.recaptchaReady && this.recaptchaSiteKeyValue) {
      try {
        const token = await grecaptcha.execute(this.recaptchaSiteKeyValue, { action: "booking_create" })
        let tokenField = this.formTarget.querySelector('input[name="g-recaptcha-response"]')
        if (!tokenField) {
          tokenField = document.createElement("input")
          tokenField.type = "hidden"
          tokenField.name = "g-recaptcha-response"
          this.formTarget.appendChild(tokenField)
        }
        tokenField.value = token
      } catch (e) {
        console.warn("reCAPTCHA execution failed, submitting with fallback:", e)
        this.addFallbackFlag()
      }
    } else {
      this.addFallbackFlag()
    }

    // Standard form submission — server saves booking and redirects to checkout
    this.formTarget.submit()
  }

  addFallbackFlag() {
    let fallback = this.formTarget.querySelector('input[name="recaptcha_fallback"]')
    if (!fallback) {
      fallback = document.createElement("input")
      fallback.type = "hidden"
      fallback.name = "recaptcha_fallback"
      this.formTarget.appendChild(fallback)
    }
    fallback.value = "true"
  }

  _resetSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.textContent = "Submit Booking Request"
    }
  }
}
